<template>
  <div class="container">
    <!-- 移动端布局 (Portrait) -->
    <div class="mobile-layout">
      <!-- 1. LED配置模块 -->
      <div class="led-control-area">
        <div class="led-connection">
          <button 
            class="led-btn"
            :class="{ 'connected': ledConnected }"
            @click="toggleLEDConnection"
            :disabled="ledConnecting">
            {{ ledConnecting ? '连接中...' : (ledConnected ? '断开LED' : '蓝牙连接LED') }}
          </button>
          <div class="led-status">
            <span class="status-indicator" :class="{ 'active': ledConnected }"></span>
            <span class="status-text">{{ ledStatusText }}</span>
          </div>
        </div>
        
        <div class="led-info" v-if="ledConnected && deviceInfo">
          <div class="device-info">
            <span class="info-label">设备:</span>
            <span class="info-value">{{ deviceInfo.deviceName }}</span>
          </div>
          <div class="device-info" v-if="deviceInfo.deviceParams">
            <span class="info-label">尺寸:</span>
            <span class="info-value">{{ deviceInfo.deviceParams.width }}x{{ deviceInfo.deviceParams.height }}</span>
          </div>
          <div class="led-controls">
            <label class="brightness-label">亮度:</label>
            <input 
              type="range" 
              min="1" 
              max="15" 
              v-model="ledBrightness" 
              @change="setBrightness"
              class="brightness-slider">
            <span class="brightness-value">{{ ledBrightness }}</span>
            <button class="clear-btn" @click="clearLEDScreen">清屏</button>
          </div>
        </div>
      </div>

      <!-- 3. 站点信息和配置模块 -->
      <div class="station-config-area">
        <!-- 品牌标题（移动端） -->
        <div class="brand-title mobile-brand">
          <img class="brand-logo" :src="baseUrl + 'icon-192x192.png'" alt="通行线" />
          <span class="brand-text">通行线报站机</span>
        </div>
        <div class="route-info">
          <h1 class="route-name">{{ routeName }}</h1>
          <div class="direction-buttons">
            <button 
              :class="['direction-btn', { active: currentDirection === 'up' }]" 
              @click="switchDirection('up')"
              :disabled="!isConfigLoaded">上行</button>
            <button 
              :class="['direction-btn', { active: currentDirection === 'down' }]" 
              @click="switchDirection('down')"
              :disabled="!isConfigLoaded || isOneWay">下行</button>
          </div>
        </div>

        <div class="station-info">
          <p class="current-station">当前站：{{ currentStationName }}</p>
          <p class="next-station" v-if="!isLastStation">下一站：{{ nextStationName }}</p>
          <p class="terminal-station" v-if="isLastStation">当前是终点站</p>
        </div>

        <div class="station-select-container">
          <label for="stationSelect">选择当前站点：</label>
          <select 
            id="stationSelect" 
            class="station-select" 
            v-model="currentStationIndex"
            @change="updateStationDisplay"
            :disabled="!isConfigLoaded">
            <option v-if="!stations.length" value="">请先上传配置压缩包</option>
            <option v-for="(station, index) in stations" :key="station.id" :value="index">
              {{ station.name }}
            </option>
          </select>
        </div>

        <!-- 全局操作按钮 -->
        <div class="global-actions-mobile">
          <button class="upload-btn" @click="uploadZipFile">上传配置压缩包</button>
          <button class="upload-btn help-btn" @click="openHelpExternal">帮助</button>
        </div>
      </div>

      <!-- 4. 核心报站操作按钮 -->
      <div class="control-buttons">
        <button class="control-btn blue" @click="handlePreAnnounce" :disabled="!isReady || isPlaying">出站</button>
        <button class="control-btn blue" @click="handleArriveAnnounce" :disabled="!isReady || isFirstStation || isPlaying">进站</button>
        <template v-if="!isPlaying">
          <button class="control-btn purple" @click="handlePrevStation" :disabled="!isReady">上一站</button>
          <button class="control-btn purple" @click="handleNextStation" :disabled="!isReady">下一站</button>
        </template>
        <button v-else class="control-btn red full-width" @click="stopCurrentAudio">停止播放</button>
      </div>

      <!-- 5. 服务用语选择按钮 -->
      <div class="service-buttons">
        <button 
          v-for="n in 10" 
          :key="n"
          class="service-btn" 
          @click="playServiceAudio(n-1)"
          :disabled="!isReady || isPlaying">
          服务语{{ n }}
        </button>
      </div>
      
      <!-- 6. 移动端版权信息 -->
      <div class="privacy-mobile">
        <a href="javascript:void(0)" @click="showPrivacy = true">© {{ currentYear }} 通行线 · 隐私政策 · V{{ appVersion }}</a>
      </div>
    </div>

    <!-- 桌面端布局 (Landscape) -->
    <div class="desktop-layout">
      <!-- 核心功能网格 (2x2) -->
      <div class="grid-container">
        <!-- 左上：LED设备控制 -->
        <div class="grid-item led-control-card">
          <h3 class="card-title">LED设备控制</h3>
          <div class="card-content">
            <div class="led-connection">
              <button 
                class="led-btn"
                :class="{ 'connected': ledConnected }"
                @click="toggleLEDConnection"
                :disabled="ledConnecting">
                {{ ledConnecting ? '连接中...' : (ledConnected ? '断开LED' : '蓝牙连接LED') }}
              </button>
              <div class="led-status">
                <span class="status-indicator" :class="{ 'active': ledConnected }"></span>
                <span class="status-text">{{ ledStatusText }}</span>
              </div>
            </div>
            
            <div class="led-info" v-if="ledConnected && deviceInfo">
              <div class="device-info">
                <span class="info-label">设备:</span>
                <span class="info-value">{{ deviceInfo.deviceName }}</span>
              </div>
              <div class="device-info" v-if="deviceInfo.deviceParams">
                <span class="info-label">尺寸:</span>
                <span class="info-value">{{ deviceInfo.deviceParams.width }}x{{ deviceInfo.deviceParams.height }}</span>
              </div>
              <div class="led-controls">
                <label class="brightness-label">亮度:</label>
                <input 
                  type="range" 
                  min="1" 
                  max="15" 
                  v-model="ledBrightness" 
                  @change="setBrightness"
                  class="brightness-slider">
                <span class="brightness-value">{{ ledBrightness }}</span>
                <button class="clear-btn" @click="clearLEDScreen">清屏</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 右上：报站控制 -->
        <div class="grid-item control-card">
          <h3 class="card-title">报站控制</h3>
          <div class="card-content">
            <div class="primary-controls">
              <button class="control-btn blue large-grid" @click="handlePreAnnounce" :disabled="!isReady || isPlaying">出站</button>
              <button class="control-btn blue large-grid" @click="handleArriveAnnounce" :disabled="!isReady || isFirstStation || isPlaying">进站</button>
            </div>
            <div class="secondary-controls" v-if="!isPlaying">
              <button class="control-btn purple" @click="handlePrevStation" :disabled="!isReady">上一站</button>
              <button class="control-btn purple" @click="handleNextStation" :disabled="!isReady">下一站</button>
            </div>
            <div class="stop-controls" v-else>
              <button class="control-btn red large-grid full-width" @click="stopCurrentAudio">停止播放</button>
            </div>
          </div>
        </div>

        <!-- 左下：线路配置 -->
        <div class="grid-item station-config-card">
          <h3 class="card-title"><img class="title-logo" :src="baseUrl + 'icon-192x192.png'" alt="通行线" /> <span class="title-text">通行线报站机</span></h3>
          <div class="card-content">
            <!-- 添加操作按钮区域 -->

            <div class="route-info-grid">
              <h1 class="route-name-grid">{{ routeName }}</h1>
              <div class="direction-buttons-grid">
                <button 
                  :class="['direction-btn', { active: currentDirection === 'up' }]" 
                  @click="switchDirection('up')"
                  :disabled="!isConfigLoaded">上行</button>
                <button 
                  :class="['direction-btn', { active: currentDirection === 'down' }]" 
                  @click="switchDirection('down')"
                  :disabled="!isConfigLoaded || isOneWay">下行</button>
              </div>
            </div>

            <div class="station-info-grid">
              <p class="current-station-grid">当前站：{{ currentStationName }}</p>
              <p class="next-station-grid" v-if="!isLastStation">下一站：{{ nextStationName }}</p>
              <p class="terminal-station-grid" v-if="isLastStation">当前是终点站</p>
            </div>

            <div class="station-select-container-grid">
              <label for="stationSelectGrid">选择当前站点：</label>
              <select 
                id="stationSelectGrid" 
                class="station-select-grid" 
                v-model="currentStationIndex"
                @change="updateStationDisplay"
                :disabled="!isConfigLoaded">
                <option v-if="!stations.length" value="">请先上传配置压缩包</option>
                <option v-for="(station, index) in stations" :key="station.id" :value="index">
                  {{ station.name }}
                </option>
              </select>
            </div>
            <div class="config-actions">
              <button class="config-btn upload" @click="uploadZipFile">上传配置压缩包</button>
              <button class="config-btn help" @click="openHelpExternal">帮助</button>
            </div>
          </div>
        </div>

        <!-- 右下：服务用语 -->
        <div class="grid-item service-card">
          <h3 class="card-title">服务用语</h3>
          <div class="card-content">
            <div class="service-buttons-grid">
              <button 
                v-for="n in 10" 
                :key="n"
                class="service-btn-grid" 
                @click="playServiceAudio(n-1)"
                :disabled="!isReady || isPlaying">
                服务语{{ n }}
              </button>
            </div>
            <div class="privacy-inline">
              <a href="javascript:void(0)" @click="showPrivacy = true">© {{ currentYear }} 通行线 · 隐私政策 · V{{ appVersion }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 帮助模态框（已改为外链，保留代码方便未来恢复）
    <Teleport to="body">
      <transition name="modal">
        <div v-if="showHelp" class="modal" style="display: block;">
          <div class="modal-content">
            <span class="close" @click="showHelp = false">&times;</span>
            <h2>使用帮助</h2>
            <div class="help-content">...</div>
          </div>
        </div>
      </transition>
    </Teleport>
    -->

    <!-- Toast 通知 -->
    <transition name="fade">
      <div v-if="toast.show" class="toast">{{ toast.message }}</div>
    </transition>
    <div v-if="showPrivacy" class="privacy-overlay" style="display: block;">
      <div class="privacy-card" role="dialog" aria-modal="true">
        <button class="privacy-close" @click="showPrivacy = false" aria-label="关闭">×</button>
        <h3 class="privacy-title">隐私政策与使用条款（通行线报站机）</h3>
        <p class="privacy-tip">重要提示：本应用的所有数据仅在本地设备与浏览器中处理和存储，不会上传到云端或任何服务器。</p>
        <div class="privacy-content">
          <p>欢迎使用通行线报站机。使用本应用即表示您接受以下条款和条件。如果您不同意这些条款，请勿使用本应用。</p>
          <p><strong>1. 软件服务</strong><br>本应用用于报站播放与LED联动显示，面向已购买通行线像素相关硬件产品的用户，提供压缩包配置导入、报站控制、服务用语播放、LED显示内容控制等功能。</p>
          <p><strong>2. 用户内容责任声明</strong><br>您通过本应用导入、创建、编辑与播放的所有内容均由您自行负责。通行线不参与、不干预您的内容创作与选择，也不对您所创建或导入内容的合法性、准确性或适当性负责。使用本应用产生的任何后果，均由您个人承担全部责任。</p>
          <p><strong>3. 用户数据与隐私</strong><br>本应用不会将任何数据上传至云端或第三方服务器。配置文件与音频文件均由您本地选择与导入；应用运行过程中的数据处理与缓存全部在本地设备/浏览器中完成。清空浏览器缓存或更换设备将导致临时数据丢失，请自行妥善保存与备份配置压缩包。</p>
          <p><strong>4. 知识产权</strong><br>您导入或创建的内容（包括但不限于文字、音频、图片等）归您或其合法权利人所有。通行线不对您的内容主张任何权利。应用本身及其源代码、界面设计与相关文档之知识产权归通行线所有。</p>
          <p><strong>5. 使用限制</strong><br>禁止使用本应用制作、传播违反法律法规或侵犯他人合法权益的内容。禁止以任何方式尝试反编译、破解、篡改或绕过本应用的技术措施，除非获得明确授权。</p>
          <p><strong>6. 免责声明</strong><br>本应用按“现状”提供，不对其功能完整性、可靠性、适用性或适配性作出任何明示或暗示的保证。因使用本应用（包括但不限于配置错误、素材版权纠纷、设备异常）造成的任何直接或间接损失，由用户自行承担。</p>
          <p><strong>7. 音频使用</strong><br>请确保您导入与播放的音频等素材拥有合法使用权，并遵守相应的许可协议与版权要求。对于用户导入素材引发的版权争议或损失，通行线不承担责任。</p>
          <p><strong>8. 协议更新</strong><br>为提升产品体验与合规性，我们可能适时更新本协议。协议更新后将以本页面的最新版本为准，继续使用本应用将视为接受更新后的协议。</p>
          <p><strong>9. 意见与建议</strong><br>如对本应用或协议有任何疑问、意见或建议，欢迎联系通行线团队。我们将基于用户反馈持续改进。</p>
        </div>
        <div class="privacy-actions">
          <button class="ok-btn" @click="showPrivacy = false">知道了</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
const JSZip = require('jszip');
const LEDController = require('./LEDController.js');

export default {
  name: 'BusAnnouncement',
  mounted() {
    // 定期检查LED连接状态，确保状态同步
    this.ledStatusTimer = setInterval(() => {
      if (this.ledController) {
        const actualConnected = this.ledController.isConnected;
        if (this.ledConnected !== actualConnected) {
          this.ledConnected = actualConnected;
          if (!actualConnected) {
            this.deviceInfo = null; // 如果连接断开，清空设备信息
          }
        }
      }
    }, 1000); // 每秒检查一次
  },
  beforeUnmount() {
    // 清理定时器
    if (this.ledStatusTimer) {
      clearInterval(this.ledStatusTimer);
    }
  },
        data() {
        return {
          // 配置数据
          configData: null,
          currentDirection: 'up',
          currentStationIndex: 0,
          audioQueue: [],
          isPlaying: false,
          currentAudio: null, // 当前音频播放器引用
          showHelp: false,
          // Toast通知
          toast: {
            show: false,
            message: '',
            timer: null
          },
      // iOS 播放策略与提示状态
      iosAudioUnlocked: false,
      iosUnlockTipShown: false,
      audioFilesByPath: new Map(), // 路径 -> 文件映射
      audioFolders: [], // 音频文件夹名列表
      audioFilesNormalizedKeyMap: new Map(), // 归一化key -> 真实key
      // LED控制相关
      // 使用 markRaw 包装 LEDController，防止 Vue 3 将其转换为响应式
      ledController: markRaw(new LEDController()),
      ledConnecting: false,
      ledConnected: false, // 添加响应式的连接状态
      deviceInfo: null,
      ledBrightness: 10,
      showPrivacy: false,
      
    };
  },
  computed: {
    // 是否已加载配置
    isConfigLoaded() {
      return this.configData !== null;
    },
    // 是否准备好播放（只依赖配置和音频文件加载）
    isReady() {
      return this.isConfigLoaded && this.audioFilesByPath && this.audioFilesByPath.size > 0;
    },
    // 是否为单向线路
    isOneWay() {
      const type = (this.configData?.lineType || '').toString().toLowerCase();
      const oneWays = new Set(['oneway', 'one_way', 'one-way', 'single']);
      const twoWays = new Set(['twoway', 'two_way', 'two-way', 'double']);
      if (oneWays.has(type)) return true;
      if (twoWays.has(type)) return false;
      return false;
    },
    // 是否为起点站
    isFirstStation() {
      return this.currentStationIndex === 0;
    },
    // 是否为终点站
    isLastStation() {
      return this.currentStationIndex === this.stations.length - 1;
    },
    // 获取当前方向的站点列表
    stations() {
      if (!this.configData) return [];
      return this.currentDirection === 'up' 
        ? this.configData.upDirectionStations 
        : (this.configData.downDirectionStations || []);
    },
    // 当前站点名称
    currentStationName() {
      if (!this.stations.length) return '暂无';
      return this.stations[this.currentStationIndex]?.name || '暂无';
    },
    // 下一站站点名称
    nextStationName() {
      if (!this.stations.length) return '暂无';
      let nextIndex = this.currentStationIndex + 1;
      if (nextIndex >= this.stations.length) {
        return '终点站';
      }
      return this.stations[nextIndex]?.name || '暂无';
    },
    // 线路名称
    routeName() {
      return this.configData?.lineName || this.configData?.route || '公交线路';
    },
    appVersion() {
      return '1.0.4';
    },
    currentYear() {
      return new Date().getFullYear();
    },
    // LED状态文本
    ledStatusText() {
      if (this.ledConnecting) return '连接中...';
      if (this.ledConnected) return '已连接';
      return '未连接';
    },
    // 是否可以发送LED指令
    canSendLED() {
      return this.ledConnected && this.deviceInfo && this.configData && this.configData.ledSettings;
    },
    // 部署在子路径时，确保公共资源路径正确
    baseUrl() {
      return process.env.BASE_URL || '/';
    }
  },
  methods: {
    openHelpExternal() {
      const url = 'https://bzdoc.tongxingxian.com/docs/bzq/01-bus-announcement-guide';
      try {
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        location.href = url;
      }
    },
    // 上传ZIP压缩包
    uploadZipFile() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.zip';
      input.addEventListener('change', this.handleZipUpload);
      input.click();
    },
    
    // 处理ZIP文件上传
    async handleZipUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.showToast('正在解析压缩包...');
      try {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);
        let jsonContent = null;
        const audioFilesByPath = new Map();
        const audioFolders = new Set();
        const audioFilesNormalizedKeyMap = new Map();
        const normalize = (str) => str.toLowerCase().replace(/（/g, '(').replace(/）/g, ')').replace(/[ _\-]/g, '').replace(/\s+/g, '');
        for (const [path, zipEntry] of Object.entries(zipContent.files)) {
          if (zipEntry.dir) {
            const folderName = decodeURIComponent(zipEntry.name.replace(/\/$/, ''));
            if (folderName) audioFolders.add(folderName);
            continue;
          }
          const fileName = decodeURIComponent(zipEntry.name.split('/').pop());
          const folderPath = decodeURIComponent(zipEntry.name.split('/').slice(0, -1).join('/'));
          if (fileName.toLowerCase().endsWith('.json')) {
            const content = await zipEntry.async('text');
            jsonContent = JSON.parse(content);
            continue;
          }
          if (/\.(wav|mp3|ogg)$/i.test(fileName)) {
            const audioBlob = await zipEntry.async('blob');
            // iOS 对 Blob URL 的 MIME 更敏感，这里按扩展名补齐类型（仅 iOS 强制）
            const ext = (fileName.split('.').pop() || '').toLowerCase();
            const mimeByExt = { mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg' };
            const mappedType = mimeByExt[ext] || '';
            const typeForFile = this.isIOS() ? (mappedType || audioBlob.type || '') : (audioBlob.type || mappedType);
            const audioFile = new File([audioBlob], fileName, { type: typeForFile });
            if (folderPath) {
              const realKey = `${folderPath}/${fileName}`.toLowerCase();
              audioFilesByPath.set(realKey, audioFile);
              // 归一化key
              const normKey = normalize(realKey);
              audioFilesNormalizedKeyMap.set(normKey, realKey);
            }
          }
        }
        if (!jsonContent) {
          this.showToast('压缩包中未找到JSON配置文件');
          return;
        }
        if (audioFilesByPath.size === 0) {
          this.showToast('压缩包中未找到任何音频文件');
          return;
        }
        this.configData = jsonContent;
        this.audioFilesByPath = audioFilesByPath;
        this.audioFolders = Array.from(audioFolders);
        this.audioFilesNormalizedKeyMap = audioFilesNormalizedKeyMap;
        this.processConfigData();
        if (this.ledConnected) {
          this.ledController.setLEDConfig(this.configData);
          // 如果LED屏已连接，发送压缩包中的欢迎语到LED屏
          try {
            await this.ledController.sendWelcomeMessage(this.currentDirection || 'up');
            console.log('成功发送压缩包中的欢迎语到LED屏');
          } catch (error) {
            console.error('发送欢迎语到LED屏失败:', error);
          }
        }
        this.showToast(`配置压缩包加载成功! 包含${audioFilesByPath.size}个音频文件`);
      } catch (error) {
        console.error('解析压缩包时出错:', error);
        this.showToast('解析压缩包失败: ' + error.message);
      }
    },
    
    // 处理配置数据
    processConfigData() {
      if (!this.configData) return;

      // 处理下行方向的站点
      if (this.isOneWay) {
        // 单向线路不生成下行站点，确保为空数组
        this.configData.downDirectionStations = [];
      } else if (!this.configData.downDirectionStations || this.configData.downDirectionStations.length === 0) {
        // 如果没有下行站点，则自动生成（使用上行反向）
        this.configData.downDirectionStations = [...this.configData.upDirectionStations].reverse().map((station, index) => {
          return {
            ...station,
            id: index + 1  // 重新分配ID
          };
        });
      }

      // 处理LED配置 - 可能在defaultConfig.ledSettings或直接在ledSettings中
      let ledSettings = null;
      
      if (this.configData.defaultConfig && this.configData.defaultConfig.ledSettings) {
        // 从defaultConfig.ledSettings中读取
        ledSettings = this.configData.defaultConfig.ledSettings;
      } else if (this.configData.ledSettings) {
        // 直接从ledSettings中读取
        ledSettings = this.configData.ledSettings;
      } else {
        this.showToast('压缩包JSON中未找到LED配置，请检查配置文件');
        console.error('压缩包JSON中缺少LED配置');
        return;
      }

      // 验证必要的LED配置字段
      if (!ledSettings.arrivalLEDContent || !ledSettings.departureLEDContent) {
        this.showToast('LED配置中缺少必要的显示内容字段(arrivalLEDContent/departureLEDContent)');
        console.error('LED配置不完整:', ledSettings);
        return;
      }

      // 将LED配置提升到顶层，方便后续使用
      this.configData.ledSettings = ledSettings;
      
      console.log('LED配置已加载:', ledSettings);

      // 重置站点索引
      this.currentStationIndex = 0;
      
      // 默认选择上行方向
      this.switchDirection('up');
    },
    
    // 切换方向（上行/下行）
    switchDirection(direction) {
      if (this.isOneWay && direction === 'down') {
        this.showToast('单向线路不支持下行');
        return;
      }
      this.currentDirection = direction;
      this.currentStationIndex = 0;
      this.updateStationDisplay();
    },
    
    // 更新站点显示
    updateStationDisplay() {
      // 在Vue的计算属性中已经处理了站点显示，这里不需要额外操作
    },
    
    // 播放预报站语音（出站）
    handlePreAnnounce() {
      if (!this.isReady) {
        this.showToast('请先上传有效的配置和音频文件');
        return;
      }
      // iOS 一次性解锁音频
      this.unlockIOSAudioOnce();
      this.stopCurrentAudio();
      const currentStationIndex = this.currentStationIndex;
      const nextStationIndex = currentStationIndex + 1;
      if (nextStationIndex >= this.stations.length) {
        // 终点站后的出站逻辑
        if (this.isOneWay) {
          // 单向线路：回到第一站进行出站
          if (this.stations.length <= 1) {
            this.showToast('该线路只有一个站点，无法出站');
            return;
          }
          // 切换到第一站的出站效果：当前站置为第二站，播放自定义出站语或欢迎语或默认出站语
          this.currentStationIndex = 1;
          this.sendLEDDeparture();
          const nextStation = this.stations[1];
          const defaultConfig = this.configData.defaultConfig;
          
          // 1. 优先检查该站是否有自定义出站语
          if (nextStation.customDeparture && nextStation.departureAudioSequence && nextStation.departureAudioSequence.length > 0) {
            this.addToQueue(nextStation.departureAudioSequence, nextStation);
          } else {
            // 2. 如果没有自定义出站语，检查是否有欢迎语序列
            let welcomeSeq = [];
            if (this.currentDirection === 'up' && defaultConfig.upWelcomeAudioSequence && defaultConfig.upWelcomeAudioSequence.length > 0) {
              welcomeSeq = defaultConfig.upWelcomeAudioSequence;
            } else if (this.currentDirection === 'down' && defaultConfig.downWelcomeAudioSequence && defaultConfig.downWelcomeAudioSequence.length > 0) {
              welcomeSeq = defaultConfig.downWelcomeAudioSequence;
            } else if (defaultConfig.welcomeAudioSequence && defaultConfig.welcomeAudioSequence.length > 0) {
              welcomeSeq = defaultConfig.welcomeAudioSequence;
            }
            if (welcomeSeq.length > 0) {
              this.addToQueue(welcomeSeq, nextStation, {isWelcome: true});
            } else {
              // 3. 如果没有欢迎语，才播放默认的出站语
              this.playTraditionalDeparture(nextStation);
            }
          }
        } else {
          // 双向线路：切换到另一方向
          this.handleTerminalDeparture();
        }
        return;
      }
      const nextStation = this.stations[nextStationIndex];
      this.currentStationIndex = nextStationIndex;
      this.sendLEDDeparture();
      // 优先播放下一站自定义出站音频
      if (nextStation.customDeparture && nextStation.departureAudioSequence && nextStation.departureAudioSequence.length > 0) {
        this.addToQueue(nextStation.departureAudioSequence, nextStation);
        return;
      }
      const defaultConfig = this.configData.defaultConfig;
      // 第一站时，先判断方向优先播放 up/downWelcomeAudioSequence
      if (currentStationIndex === 0) {
        let welcomeSeq = [];
        if (this.currentDirection === 'up' && defaultConfig.upWelcomeAudioSequence && defaultConfig.upWelcomeAudioSequence.length > 0) {
          welcomeSeq = defaultConfig.upWelcomeAudioSequence;
        } else if (this.currentDirection === 'down' && defaultConfig.downWelcomeAudioSequence && defaultConfig.downWelcomeAudioSequence.length > 0) {
          welcomeSeq = defaultConfig.downWelcomeAudioSequence;
        } else if (defaultConfig.welcomeAudioSequence && defaultConfig.welcomeAudioSequence.length > 0) {
          welcomeSeq = defaultConfig.welcomeAudioSequence;
        }
        if (welcomeSeq.length > 0) {
          this.addToQueue(welcomeSeq, nextStation, {isWelcome: true});
          return;
        }
      }
      // 从第二站开始才播放常规出站语音
      let seq = [];
      if (this.currentDirection === 'up' && defaultConfig.upDepartureAudioSequence && defaultConfig.upDepartureAudioSequence.length > 0) {
        seq = defaultConfig.upDepartureAudioSequence;
      } else if (this.currentDirection === 'down' && defaultConfig.downDepartureAudioSequence && defaultConfig.downDepartureAudioSequence.length > 0) {
        seq = defaultConfig.downDepartureAudioSequence;
      } else if (defaultConfig.departureAudioSequence && defaultConfig.departureAudioSequence.length > 0) {
        seq = defaultConfig.departureAudioSequence;
      }
      if (seq.length > 0) {
        this.addToQueue(seq, nextStation);
      } else {
        this.showToast('未找到出站语音配置');
      }
    },
    
    // 播放传统的出站语音
    playTraditionalDeparture(nextStation) {
      // 只按方向选 departureAudioSequence
      const defaultConfig = this.configData.defaultConfig;
      let seq = [];
      if (this.currentDirection === 'up' && defaultConfig.upDepartureAudioSequence && defaultConfig.upDepartureAudioSequence.length > 0) {
        seq = defaultConfig.upDepartureAudioSequence;
      } else if (this.currentDirection === 'down' && defaultConfig.downDepartureAudioSequence && defaultConfig.downDepartureAudioSequence.length > 0) {
        seq = defaultConfig.downDepartureAudioSequence;
      } else if (defaultConfig.departureAudioSequence && defaultConfig.departureAudioSequence.length > 0) {
        seq = defaultConfig.departureAudioSequence;
      }
      if (seq.length > 0) {
        this.addToQueue(seq, nextStation);
      } else {
        this.showToast('未找到出站语音配置');
      }
    },
    
    // 处理终点站出站逻辑
    handleTerminalDeparture() {
      if (this.isOneWay) {
        // 单向线路不反向，实际处理在handlePreAnnounce中已完成
        return;
      }
      // 切换到另一个方向
      const newDirection = this.currentDirection === 'up' ? 'down' : 'up';
      this.switchDirection(newDirection);
      // 设置为新方向的起点站（索引为0）
      this.currentStationIndex = 0;
      // 获取新方向起点站的信息
      const newSecondStation = this.stations.length > 1 ? this.stations[1] : null;
      if (newSecondStation) {
        // 切换到第二站，按优先级播放音频：自定义出站语 → 欢迎语 → 默认出站语
        this.currentStationIndex = 1;
        
        const defaultConfig = this.configData.defaultConfig;
        
        // 1. 优先检查该站是否有自定义出站语
        if (newSecondStation.customDeparture && newSecondStation.departureAudioSequence && newSecondStation.departureAudioSequence.length > 0) {
          this.addToQueue(newSecondStation.departureAudioSequence, newSecondStation);
        } else {
          // 2. 如果没有自定义出站语，检查是否有欢迎语配置
          let welcomeSequence = [];
          
          // 优先检查方向特定的欢迎语
          if (newDirection === 'up' && defaultConfig.upWelcomeAudioSequence && defaultConfig.upWelcomeAudioSequence.length > 0) {
            welcomeSequence = defaultConfig.upWelcomeAudioSequence;
          } else if (newDirection === 'down' && defaultConfig.downWelcomeAudioSequence && defaultConfig.downWelcomeAudioSequence.length > 0) {
            welcomeSequence = defaultConfig.downWelcomeAudioSequence;
          } else if (defaultConfig.welcomeAudioSequence && defaultConfig.welcomeAudioSequence.length > 0) {
            // 如果没有方向特定的欢迎语，使用通用欢迎语
            welcomeSequence = defaultConfig.welcomeAudioSequence;
          }
          
          if (welcomeSequence.length > 0) {
            // 播放欢迎语音序列
            this.addToQueue(welcomeSequence, newSecondStation, {isWelcome: true});
          } else {
            // 3. 如果没有欢迎语，才播放默认的出站语
            this.playTraditionalDeparture(newSecondStation);
          }
        }
        
        // 新增：切换方向后立即发送LED出站信息
        this.sendLEDDeparture();
        this.showToast(`已切换到${newDirection === 'up' ? '上行' : '下行'}方向`);
      } else {
        this.showToast('该方向只有一个站点，无法播放出站语音');
      }
    },

    // 播放到站语音（进站）
    handleArriveAnnounce() {
      if (!this.isReady) {
        this.showToast('请先上传有效的配置和音频文件');
        return;
      }
      // iOS 一次性解锁音频
      this.unlockIOSAudioOnce();
      this.stopCurrentAudio();
      const currentStation = this.stations[this.currentStationIndex];
      this.sendLEDArrival();
      const defaultConfig = this.configData.defaultConfig;
      // 终点站：优先播放【终点站自定义进站音频】，再回落到默认终点站语音
      if (this.currentStationIndex === this.stations.length - 1) {
        // 1) 先检查终点站是否有自定义进站语音
        if (currentStation.customArrival && currentStation.arrivalAudioSequence && currentStation.arrivalAudioSequence.length > 0) {
          this.addToQueue(currentStation.arrivalAudioSequence, currentStation);
          return;
        }
        // 2) 回落到默认终点站语音（按方向）
        let seq = [];
        if (this.currentDirection === 'up' && defaultConfig.upTerminalAudioSequence && defaultConfig.upTerminalAudioSequence.length > 0) {
          seq = defaultConfig.upTerminalAudioSequence;
        } else if (this.currentDirection === 'down' && defaultConfig.downTerminalAudioSequence && defaultConfig.downTerminalAudioSequence.length > 0) {
          seq = defaultConfig.downTerminalAudioSequence;
        } else if (defaultConfig.terminalAudioSequence && defaultConfig.terminalAudioSequence.length > 0) {
          seq = defaultConfig.terminalAudioSequence;
        }
        if (seq.length > 0) {
          this.addToQueue(seq, currentStation);
          return;
        }
      }
      // 非终点站或终点站无自定义与默认终点语音：优先播放站点自定义进站音频
      if (currentStation.customArrival && currentStation.arrivalAudioSequence && currentStation.arrivalAudioSequence.length > 0) {
        this.addToQueue(currentStation.arrivalAudioSequence, currentStation);
        return;
      }
      let seq = [];
      if (this.currentDirection === 'up' && defaultConfig.upArrivalAudioSequence && defaultConfig.upArrivalAudioSequence.length > 0) {
        seq = defaultConfig.upArrivalAudioSequence;
      } else if (this.currentDirection === 'down' && defaultConfig.downArrivalAudioSequence && defaultConfig.downArrivalAudioSequence.length > 0) {
        seq = defaultConfig.downArrivalAudioSequence;
      } else if (defaultConfig.arrivalAudioSequence && defaultConfig.arrivalAudioSequence.length > 0) {
        seq = defaultConfig.arrivalAudioSequence;
      }
      if (seq.length > 0) {
        this.addToQueue(seq, currentStation);
      } else {
        this.showToast('未找到进站语音配置');
      }
    },
    
    // 切换到上一站
    handlePrevStation() {
      if (!this.isReady) {
        this.showToast('请先上传有效的配置和音频文件');
        return;
      }
      if (this.currentStationIndex > 0) {
        this.currentStationIndex--;
      } else {
        this.showToast('已经是第一站');
      }
    },
    
    // 切换到下一站
    handleNextStation() {
      if (!this.isReady) {
        this.showToast('请先上传有效的配置和音频文件');
        return;
      }
      if (this.currentStationIndex < this.stations.length - 1) {
        this.currentStationIndex++;
      } else {
        this.showToast('已经是最后一站');
      }
    },
    
    // 播放服务语音频
    playServiceAudio(index) {
      if (!this.isReady) {
        this.showToast('请先上传有效的配置和音频文件');
        return;
      }
      // iOS 一次性解锁音频
      this.unlockIOSAudioOnce();
      this.stopCurrentAudio();
      const serviceSequences = this.configData.defaultConfig?.serviceAudioSequences;
      const hasAudio = serviceSequences && serviceSequences[index] && serviceSequences[index].length > 0;
      
      // 检查是否有LED文字内容
      const hasLEDContent = this.checkServiceLEDContent(index + 1);
      
      if (!hasAudio && !hasLEDContent) {
        // 既没有音频也没有LED内容
        this.showToast(`服务语${index + 1}没有设置音频和LED内容`);
        return;
      }
      
      // 如果有LED内容，发送到LED屏（无论是否有音频）
      if (hasLEDContent) {
        this.sendLEDService(index + 1);
      }
      
      // 如果有音频，播放音频
      if (hasAudio) {
        const currentStation = this.stations[this.currentStationIndex];
        this.addToQueue(serviceSequences[index], currentStation);
      } else {
        // 只有LED内容，没有音频，显示提示
        this.showToast(`服务语${index + 1}仅发送LED内容，无音频`);
      }
    },
    
    // 检查服务语是否有LED内容
    checkServiceLEDContent(serviceNumber) {
      if (!this.configData || !this.configData.ledSettings) {
        return false;
      }
      
      const settings = this.configData.ledSettings;
      const languageConfig = this.ledController?.getLanguageConfig ? 
        this.ledController.getLanguageConfig() : 
        { languages: [{code: 'zh', name: '中文'}] };
      
      // 检查是否有任何语言的LED内容
      for (const language of languageConfig.languages || []) {
        let fieldName;
        if (language.code === 'zh') {
          fieldName = `service${serviceNumber}LEDContent`;
        } else {
          fieldName = `service${serviceNumber}LEDContent_${language.code}`;
        }
        
        if (settings[fieldName] && settings[fieldName].trim()) {
          return true;
        }
      }
      
      return false;
    },
    
    // 播放音频队列
    playAudioQueue(queue) {
      if (!queue || queue.length === 0) return;
      
      this.audioQueue = [...queue];
      this.isPlaying = true;
      
      this.playNextInQueue();
    },

    // 智能音频文件查找函数 - 支持路径解析
    findAudioFileByPath(audioPath) {
      if (!audioPath) return null;
      // 只支持“文件夹/文件名”路径
      return this.audioFilesByPath.get(audioPath.toLowerCase()) || null;
    },

    // 新的占位符处理：只支持【文件夹名-站名】格式
    resolvePlaceholder(item, nextStation) {
      // 只处理【文件夹名-xxx】
      const match = /^【([^\-【】]+)-([^【】]+)】$/.exec(item);
      if (match) {
        const folder = match[1];
        let name = match[2];
        // 如果是“当前站”，用 nextStation.name 替换
        if (name === '当前站' && nextStation) name = nextStation.name;
        // 支持mp3/wav/ogg
        const tryExts = ['.mp3', '.wav', '.ogg'];
        for (const ext of tryExts) {
          const path = `${folder}/${name}${ext}`.toLowerCase();
          const file = this.audioFilesByPath.get(path);
          if (file) return file;
        }
      }
      return null;
    },

    // 处理单个音频项目（用于处理stationAudioSequence中的项目）
    processAudioItem(item, nextStation, normalize, stations, firstStation, lastStation, nextStationOfNext) {
      const processedFiles = [];
      let audioFile = null;
      let realKey = null;
      
      // 路径格式
      if (/^[^【】]+\/(.+)\.(mp3|wav|ogg)$/i.test(item)) {
        const normKey = normalize(item);
        realKey = this.audioFilesNormalizedKeyMap.get(normKey);
        if (realKey) audioFile = this.audioFilesByPath.get(realKey);
      }
      
      // 新占位符格式
      if (!audioFile && /^【[^-【】]+-[^【】]+】$/.test(item)) {
        const match = /^【([^\-【】]+)-([^【】]+)】$/.exec(item);
        if (match) {
          let folder = normalize(match[1]);
          let name = match[2];
          if (name === '当前站' && nextStation) name = nextStation.name;
          if (name === '起点站') name = firstStation;
          if (name === '终点站') name = lastStation;
          if (name === '前方站') name = nextStationOfNext;
          name = normalize(name);
          const tryExts = ['.mp3', '.wav', '.ogg'];
          for (const ext of tryExts) {
            const normKey = `${folder}/${name}${ext}`;
            realKey = this.audioFilesNormalizedKeyMap.get(normKey);
            if (realKey) { audioFile = this.audioFilesByPath.get(realKey); break; }
          }
        }
      }
      
      // 兼容老格式
      if (!audioFile && /^【[^-【】]+\([^【】]+\)-[^【】]+】$/.test(item)) {
        const match = /^【([^-(【】]+)\(([^【】]+)\)-([^【】]+)】$/.exec(item);
        if (match) {
          let folder = normalize(`${match[1]}(${match[2]})`);
          let name = match[3];
          if (name === '当前站' && nextStation) name = nextStation.name;
          if (name === '起点站') name = firstStation;
          if (name === '终点站') name = lastStation;
          if (name === '前方站') name = nextStationOfNext;
          name = normalize(name);
          const tryExts = ['.mp3', '.wav', '.ogg'];
          for (const ext of tryExts) {
            const normKey = `${folder}/${name}${ext}`;
            realKey = this.audioFilesNormalizedKeyMap.get(normKey);
            if (realKey) { audioFile = this.audioFilesByPath.get(realKey); break; }
          }
        }
      }
      
      if (audioFile) {
        processedFiles.push(audioFile);
      } else {
        this.showToast(`音频文件未找到: ${item}`);
      }
      
      return processedFiles;
    },

    // 只做严格路径和占位符查找，归一化兼容各种命名差异
    addToQueue(sequences, nextStation, options = {}) {
      // 每次调用前清空队列，保证只播放本次序列
      this.audioQueue = [];
      const queue = [];
      const normalize = (str) => str.toLowerCase().replace(/（/g, '(').replace(/）/g, ')').replace(/[ _\-]/g, '').replace(/\s+/g, '');
      const stations = this.stations;
      const firstStation = stations[0]?.name || '';
      const lastStation = stations[stations.length - 1]?.name || '';
      // 计算前方站（nextStation的下一站）
      let nextStationOfNext = '';
      if (nextStation) {
        const currentIndex = stations.findIndex(s => s.id === nextStation.id);
        if (currentIndex >= 0 && currentIndex < stations.length - 1) {
          nextStationOfNext = stations[currentIndex + 1]?.name || '';
        }
      }
      sequences.forEach(item => {
        let audioFile = null;
        let realKey = null;
        // 路径格式
        if (/^[^【】]+\/(.+)\.(mp3|wav|ogg)$/i.test(item)) {
          const normKey = normalize(item);
          realKey = this.audioFilesNormalizedKeyMap.get(normKey);
          if (realKey) audioFile = this.audioFilesByPath.get(realKey);
        }
        // 新占位符格式
        if (!audioFile && /^【[^-【】]+-[^【】]+】$/.test(item)) {
          const match = /^【([^\-【】]+)-([^【】]+)】$/.exec(item);
          if (match) {
            let folder = normalize(match[1]);
            let name = match[2];
            
            // 处理"当前站"：优先检查站点是否有stationAudioSequence
            if (name === '当前站' && nextStation) {
              if (nextStation.stationAudioSequence && nextStation.stationAudioSequence.length > 0) {
                // 如果站点有stationAudioSequence，递归处理这个序列
                nextStation.stationAudioSequence.forEach(stationItem => {
                  const processedFiles = this.processAudioItem(stationItem, nextStation, normalize, stations, firstStation, lastStation, nextStationOfNext);
                  processedFiles.forEach(file => {
                    if (file) queue.push(file);
                  });
                });
                return; // 跳过当前项的默认处理
              } else {
                name = nextStation.name;
              }
            }
            if (name === '起点站') name = firstStation;
            if (name === '终点站') name = lastStation;
            if (name === '前方站') name = nextStationOfNext;
            name = normalize(name);
            const tryExts = ['.mp3', '.wav', '.ogg'];
            for (const ext of tryExts) {
              const normKey = `${folder}/${name}${ext}`;
              realKey = this.audioFilesNormalizedKeyMap.get(normKey);
              if (realKey) { audioFile = this.audioFilesByPath.get(realKey); break; }
            }
          }
        }
        // 兼容老格式
        if (!audioFile && /^【[^-【】]+\([^【】]+\)-[^【】]+】$/.test(item)) {
          const match = /^【([^-(【】]+)\(([^【】]+)\)-([^【】]+)】$/.exec(item);
          if (match) {
            let folder = normalize(`${match[1]}(${match[2]})`);
            let name = match[3];
            
            // 处理"当前站"：优先检查站点是否有stationAudioSequence
            if (name === '当前站' && nextStation) {
              if (nextStation.stationAudioSequence && nextStation.stationAudioSequence.length > 0) {
                // 如果站点有stationAudioSequence，递归处理这个序列
                nextStation.stationAudioSequence.forEach(stationItem => {
                  const processedFiles = this.processAudioItem(stationItem, nextStation, normalize, stations, firstStation, lastStation, nextStationOfNext);
                  processedFiles.forEach(file => {
                    if (file) queue.push(file);
                  });
                });
                return; // 跳过当前项的默认处理
              } else {
                name = nextStation.name;
              }
            }
            if (name === '起点站') name = firstStation;
            if (name === '终点站') name = lastStation;
            if (name === '前方站') name = nextStationOfNext;
            name = normalize(name);
            const tryExts = ['.mp3', '.wav', '.ogg'];
            for (const ext of tryExts) {
              const normKey = `${folder}/${name}${ext}`;
              realKey = this.audioFilesNormalizedKeyMap.get(normKey);
              if (realKey) { audioFile = this.audioFilesByPath.get(realKey); break; }
            }
          }
        }
        // 找不到就跳过
        if (audioFile) {
          queue.push(audioFile);
        } else {
          this.showToast(`音频文件未找到: ${item}`);
        }
      });
      this.audioQueue = [...queue];
      console.log('addToQueue: audioQueue =', this.audioQueue.map(f => f?.name));
      // 立即触发播放，无论 isPlaying 状态
      this.isPlaying = false;
      this.playNextInQueue();
    },

    // 停止当前正在播放的音频
    stopCurrentAudio() {
      // 停止当前正在播放的音频并清理资源
      if (this.currentAudio) {
        this.currentAudio.pause();
        URL.revokeObjectURL(this.currentAudio.src);
      }
      // 重置所有状态
      this.currentAudio = null;
      this.audioQueue = [];
      this.isPlaying = false;
    },

    // 播放队列中的下一个音频，保证顺序递归
    playNextInQueue() {
      if (this.audioQueue.length === 0) {
        this.isPlaying = false;
        if (this.currentAudio) {
          this.currentAudio.pause();
          URL.revokeObjectURL(this.currentAudio.src);
        }
        this.currentAudio = null;
        return;
      }
      const nextAudioFile = this.audioQueue.shift();
      try {
        // iOS 兜底：若为 mp3 且缺少类型，按 audio/mpeg 重包一层 Blob
        let sourceBlob = nextAudioFile;
        const lowerName = (nextAudioFile && nextAudioFile.name) ? nextAudioFile.name.toLowerCase() : '';
        if (this.isIOS() && lowerName.endsWith('.mp3') && (!nextAudioFile.type || nextAudioFile.type === '')) {
          try {
            sourceBlob = new Blob([nextAudioFile], { type: 'audio/mpeg' });
          } catch (e) {
            // 保底不影响非关键路径
          }
        }
        const audioUrl = URL.createObjectURL(sourceBlob);
        const audio = new Audio(audioUrl);
        // iOS 友好参数
        try { audio.playsInline = true; } catch (e) {}
        try { audio.preload = 'auto'; } catch (e) {}
        if (this.currentAudio) {
          this.currentAudio.pause();
          URL.revokeObjectURL(this.currentAudio.src);
        }
        this.currentAudio = audio;
        this.isPlaying = true;
        console.log('playNextInQueue: 播放', nextAudioFile.name, 'type=', nextAudioFile.type || '(empty)');
        audio.addEventListener('ended', () => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          this.playNextInQueue();
        });
        audio.addEventListener('error', (e) => {
          console.error('Audio error on iOS?', {
            name: nextAudioFile && nextAudioFile.name,
            type: nextAudioFile && nextAudioFile.type,
            audioError: audio && audio.error && audio.error.code,
            event: e
          });
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          this.playNextInQueue();
        });
        audio.play().catch(error => {
          console.error('audio.play() failed:', error && error.name, error && error.message);
          if (this.isIOS() && error && (error.name === 'NotAllowedError' || error.name === 'NotSupportedError')) {
            if (!this.iosUnlockTipShown) {
              this.showToast('需要点击任意按钮以启用音频播放');
              this.iosUnlockTipShown = true;
            }
          }
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          this.playNextInQueue();
        });
      } catch (error) {
        this.currentAudio = null;
        this.playNextInQueue();
      }
    },

    // iOS 与能力探测工具
    isIOS() {
      try {
        const ua = navigator.userAgent || '';
        const platform = navigator.platform || '';
        const maxTouchPoints = navigator.maxTouchPoints || 0;
        return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
      } catch (_) {
        return false;
      }
    },
    canPlay(type) {
      try {
        const a = document.createElement('audio');
        return !!a.canPlayType && a.canPlayType(type) !== '';
      } catch (_) {
        return false;
      }
    },
    unlockIOSAudioOnce() {
      if (!this.isIOS() || this.iosAudioUnlocked) return;
      try {
        const a = new Audio();
        a.muted = true;
        try { a.playsInline = true; } catch (e) {}
        // 极短静音 WAV 数据
        a.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAAB3AQACABAAZGF0YQAAAA==';
        a.play().catch(() => {}).finally(() => { this.iosAudioUnlocked = true; });
      } catch (e) {
        this.iosAudioUnlocked = true;
      }
    },
    
    // 显示提示信息
    showToast(message, duration = 3000) {
      // 清除之前的定时器
      if (this.toast.timer) {
        clearTimeout(this.toast.timer);
      }
      
      // 设置新的消息
      this.toast.message = message;
      this.toast.show = true;
      
      // 定时隐藏
      this.toast.timer = setTimeout(() => {
        this.toast.show = false;
      }, duration);
    },

    handlePreReportStation() {
      if (this.currentStationIndex < this.stations.length - 1) {
        this.currentStationIndex += 1; // 先切换到下一站
        this.handleArriveAnnounce();   // 用新的 currentStation 播放预报站语音
      }
    },

    // LED连接控制
    async toggleLEDConnection() {
      if (this.ledConnected) {
        await this.disconnectLED();
      } else {
        await this.connectLED();
      }
    },

    // 连接LED（简化版本，参照项目3）
    async connectLED() {
      this.ledConnecting = true;
      
      try {
        // 设置设备信息更新回调
        this.ledController.onDeviceInfoUpdate = () => {
          console.log('App.vue: 收到设备信息更新通知');
          this.deviceInfo = this.ledController.getDeviceInfo();
          console.log('App.vue: deviceInfo 已更新', this.deviceInfo);
        };
        
        // 直接调用连接方法，不使用超时机制
        await this.ledController.connect();
        
        // 获取设备信息
        this.deviceInfo = this.ledController.getDeviceInfo();
        
        // 检查连接状态
        if (this.ledController.isConnected) {
          this.ledConnected = true;
          
          // 设置LED配置（如果有配置的话）
          if (this.configData && this.configData.ledSettings) {
            this.ledController.setLEDConfig(this.configData);
          }
          
          this.showToast('LED连接成功');
        } else {
          throw new Error('连接失败');
        }
      } catch (error) {
        this.ledConnected = false;
        this.showToast('LED连接失败: ' + error.message);
      } finally {
        this.ledConnecting = false;
      }
    },

    // 断开LED连接
    async disconnectLED() {
      try {
        await this.ledController.disconnect();
        this.ledConnected = false; // 立即更新连接状态
        this.deviceInfo = null;
        this.showToast('LED已断开连接');
      } catch (error) {
        this.ledConnected = false; // 确保连接状态为失败
        this.showToast('断开LED连接失败: ' + error.message);
      }
    },

    // 设置亮度
    async setBrightness() {
      if (this.ledConnected) {
        try {
          await this.ledController.setBrightness(this.ledBrightness);
        } catch (error) {
          this.showToast('设置亮度失败: ' + error.message);
        }
      }
    },

    // 清除LED屏幕
    async clearLEDScreen() {
      if (this.ledConnected) {
        try {
          await this.ledController.clearScreen();
          this.showToast('LED屏幕已清除');
        } catch (error) {
          this.showToast('清除LED屏幕失败: ' + error.message);
        }
      }
    },

    // 发送LED出站信息
    async sendLEDDeparture() {
      if (!this.canSendLED) return;
      
      try {
        const stationName = this.currentStationName;
        const currentStation = this.stations[this.currentStationIndex];
        await this.ledController.sendStationInfo(stationName, false, currentStation, this.currentDirection, this.currentStationIndex);
      } catch (error) {
        console.error('发送LED出站信息失败:', error);
        this.showToast('发送LED出站信息失败: ' + error.message);
      }
    },

    // 发送LED进站信息
    async sendLEDArrival() {
      if (!this.canSendLED) return;
      
      try {
        const stationName = this.currentStationName;
        const currentStation = this.stations[this.currentStationIndex];
        await this.ledController.sendStationInfo(stationName, true, currentStation, this.currentDirection, this.currentStationIndex);
      } catch (error) {
        console.error('发送LED进站信息失败:', error);
        this.showToast('发送LED进站信息失败: ' + error.message);
      }
    },

    // 发送LED服务语信息
    async sendLEDService(serviceNumber) {
      console.log('sendLEDService调用，参数:', serviceNumber);
      console.log('canSendLED:', this.canSendLED);
      console.log('ledConnected:', this.ledConnected);
      console.log('deviceInfo:', this.deviceInfo);
      console.log('configData.ledSettings存在:', !!(this.configData && this.configData.ledSettings));
      
      if (!this.canSendLED) {
        console.log('不满足发送LED条件，终止发送');
        return;
      }
      
      try {
        // 获取当前站点名称、方向和站点数据
        const stationName = this.currentStationName;
        const currentStation = this.stations[this.currentStationIndex];
        const currentDirection = this.currentDirection;
        
        console.log('发送服务语时的站点信息:', {
          stationName,
          currentDirection,
          stationIndex: this.currentStationIndex
        });
        
        // 传递当前站点和方向信息到LED控制器
        await this.ledController.sendServiceMessage(serviceNumber, stationName, currentDirection, currentStation);
        console.log(`LED服务语${serviceNumber}信息已发送`);
        this.showToast(`LED服务语${serviceNumber}已发送`);
      } catch (error) {
        console.error('发送LED服务语信息失败:', error);
        this.showToast('发送LED服务语信息失败: ' + error.message);
      }
    },
    

  }
};
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.red {
  background-color: #dc3545;
  color: white;
}

.red:hover:not(:disabled) {
  background-color: #c82333;
}

.red:disabled {
  background-color: #dc354580;
  cursor: not-allowed;
}

.control-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.control-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
  min-width: calc(25% - 8px); /* 确保每个按钮至少占据1/4的宽度减去间距 */
}

.full-width {
  flex: 2; /* 让停止播放按钮占据两个按钮的宽度 */
  width: 100%; /* 确保按钮占满整个宽度 */
}

.clear-btn {
  padding: 8px 12px;
  margin: 0 5px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  background-color: #007bff;
  color: white;
  transition: background-color 0.3s;
}

.clear-btn:hover {
  background-color: #0056b3;
}
/* 隐私政策：底部链接与紧凑弹层（不滚动页面主体） */
.service-card .card-content { position: relative; padding-bottom: 28px; }
.privacy-inline {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 6px;
  text-align: center;
}
.privacy-inline a { color: #888; text-decoration: none; opacity: .85; font-size: 12px; }
.privacy-inline a:hover { text-decoration: underline; }
.privacy-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1000;
}
/* 统一“上传/帮助”按钮居中与去下划线（兼容 a 与 button） */
.upload-btn,
.config-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}
.upload-btn:hover,
.config-btn:hover {
  text-decoration: none;
}
.privacy-card {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(680px, 92vw);
  max-height: 80vh;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,.2);
  padding: 14px 16px 12px 16px;
  display: flex;
  flex-direction: column;
  color: #212529;
}
.privacy-title { margin: 0 24px 6px 0; font-size: 16px; color: #0f172a; }
.privacy-tip { margin: 2px 0 8px 0; font-size: 12px; color: #475569; }
.privacy-close {
  position: absolute;
  right: 10px;
  top: 8px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.privacy-content { overflow: auto; padding-right: 4px; color: #1f2937; }
.privacy-content p { margin: 6px 0; }
.privacy-actions { text-align: right; margin-top: 8px; }
.ok-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: #6c757d;
  color: #fff;
  cursor: pointer;
}
.ok-btn:hover { background: #5a6268; }
</style> 
